import { useAtom } from 'jotai';
import React, {
  createContext,
  useContext,
  useCallback,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';

import { CountryCode } from 'common/api-types/common';
import { Country, Warehouse } from 'common/api-types/director';
import { useCountriesSuspenseQuery } from 'common/hooks/useCountries';
import { useAllWarehousesSuspenseQuery } from 'common/hooks/useWarehousesQuery';
import { selectedWarehouseAtom } from 'common/state/warehouseState';

/**
 * A warehouse enriched with country code and default language code.
 */
export type EnrichedWarehouse = Warehouse & {
  countryCode: CountryCode;
  defaultLanguageCode?: string;
};

const getCountryDefaultLanguage = (countries: Country[] | undefined, countryCode: CountryCode) => {
  const warehouseCountry = countries?.find((country) => country.countryCode === countryCode);
  return warehouseCountry?.defaultLanguageCode;
};

type WarehouseContextType = {
  warehouse: EnrichedWarehouse | undefined;
  onWarehouseChange: (warehouseId?: string) => void;
};

const WarehouseContext = createContext<WarehouseContextType>({
  warehouse: undefined,
  onWarehouseChange: () => {},
});

/**
 * Hook that provides common warehouse data and utilities.
 */
const useWarehouseData = () => {
  const { data: warehouses } = useAllWarehousesSuspenseQuery();
  const { data: countryData } = useCountriesSuspenseQuery();

  // Memoize the warehouses map to avoid recreating it on every render
  const warehousesMap = useMemo(() => {
    return new Map(warehouses.items.map((w) => [w.id, w]));
  }, [warehouses.items]);

  return {
    warehouses,
    countryData,
    warehousesMap,
  };
};

/**
 * Hook that creates an enriched warehouse from a base warehouse.
 */
const useEnrichedWarehouse = (warehouse: Warehouse | undefined) => {
  const { countryData } = useWarehouseData();

  const countryCode = warehouse?.countryCode as CountryCode;
  const defaultLanguageCode = getCountryDefaultLanguage(countryData, countryCode);

  // Memoize the enriched warehouse to prevent unnecessary rerenders
  const enrichedWarehouse: EnrichedWarehouse | undefined = useMemo(() => {
    if (!warehouse) return undefined;
    return { ...warehouse, countryCode, defaultLanguageCode };
  }, [warehouse, countryCode, defaultLanguageCode]);

  return enrichedWarehouse;
};

/**
 * Hook that creates a context value with memoization.
 */
const useWarehouseContextValue = (
  warehouse: EnrichedWarehouse | undefined,
  onWarehouseChange: (warehouseId?: string) => void,
) => {
  return useMemo(
    () => ({
      warehouse,
      onWarehouseChange,
    }),
    [warehouse, onWarehouseChange],
  );
};

type LocalStorageWarehouseProviderProps = {
  children: ReactNode;
};

/**
 * App-wide WarehouseContext that persists the warehouse ID to localStorage.
 */
export const LocalStorageWarehouseProvider: React.FC<LocalStorageWarehouseProviderProps> = ({
  children,
}) => {
  const { warehouses, warehousesMap } = useWarehouseData();
  const [persistedWarehouse, setPersistedWarehouse] = useAtom(selectedWarehouseAtom);

  const warehouse = warehouses.items.find((w) => w.id === persistedWarehouse?.id);
  const enrichedWarehouse = useEnrichedWarehouse(warehouse);

  // clear any persisted warehouse value that we cannot find in the current list of warehouses
  useEffect(() => {
    if (!enrichedWarehouse) {
      setPersistedWarehouse(undefined);
    }
  }, [enrichedWarehouse, setPersistedWarehouse]);

  const onWarehouseChange = useCallback(
    (newWarehouseId?: string) => {
      if (!newWarehouseId) {
        setPersistedWarehouse(undefined);
        return;
      }

      // check that the warehouse exists. Choosing a non-existent warehouse will result in choosing "no warehouse".
      const foundWarehouse = warehousesMap.get(newWarehouseId);
      setPersistedWarehouse(foundWarehouse ? { id: foundWarehouse.id } : undefined);
    },
    [warehousesMap, setPersistedWarehouse],
  );

  const contextValue = useWarehouseContextValue(enrichedWarehouse, onWarehouseChange);

  return <WarehouseContext.Provider value={contextValue}>{children}</WarehouseContext.Provider>;
};

type UrlWarehouseProviderProps = {
  /**
   * The warehouse ID currently in the URL.
   */
  warehouseId: string | undefined;
  children: (warehouse: EnrichedWarehouse) => ReactNode;
  /**
   * Rendered if no warehouse is found.
   */
  fallback?: ReactNode | null;
  /**
   * Callback for when the warehouse changes. Should only be called when the warehouse is changed explicitly,
   * e.g. when the user selects a warehouse from the warehouse picker.
   * @param warehouseId The warehouseId of the warehouse that was selected, or undefined if the warehouse was deselected. Is always a valid warehouse ID.
   */
  onWarehouseChange: (warehouseId?: string) => void;
  /**
   * Callback for when the warehouse is invalid. Should only be called when the warehouse is invalid,
   * e.g. when the warehouse is not found in the current list of warehouses.
   */
  onInvalidWarehouseId: () => void;
};

/**
 * View-specific WarehouseContext that operates based off of the warehouse ID in the URL.
 * This provider MAY be nested in another WarehouseContext, for e.g. persisting
 * the warehouse ID from the URL to localStorage or similar.
 */
export const UrlWarehouseProvider: React.FC<UrlWarehouseProviderProps> = ({
  warehouseId: warehouseIdFromUrl,
  children,
  fallback = null,
  onWarehouseChange: onWarehouseChangeProp,
  onInvalidWarehouseId,
}) => {
  const { warehouses, warehousesMap } = useWarehouseData();

  const warehouseFromUrl = warehouses.items.find((w) => w.id === warehouseIdFromUrl);
  const enrichedWarehouseFromUrl = useEnrichedWarehouse(warehouseFromUrl);

  // If we are nested in another WarehouseContext, we can fall back to it needed
  const parentWarehouseCtx = useWarehouseContext();
  const enrichedWarehouse = enrichedWarehouseFromUrl || parentWarehouseCtx.warehouse;

  // If we can't find the URL warehouse in the current list of warehouses, we should let the caller know
  useEffect(() => {
    if (warehouseIdFromUrl && !warehouseFromUrl) {
      onInvalidWarehouseId();
    }
  }, [warehouseFromUrl, onInvalidWarehouseId, warehouseIdFromUrl]);

  // This is called when a child explicitly wants to change the currently selected warehouse
  const onWarehouseChange = useCallback(
    (newWarehouseId?: string) => {
      // Find the warehouse if an ID was provided, otherwise undefined
      const foundWarehouse = newWarehouseId ? warehousesMap.get(newWarehouseId) : undefined;

      // If we have chosen a warehouse ID that is valid, we should propagate it to the parent context
      if (newWarehouseId && foundWarehouse) {
        parentWarehouseCtx.onWarehouseChange(foundWarehouse.id);
      } else if (!newWarehouseId) {
        // If we are explicitly deselecting the warehouse, we should also deselect it in the parent context
        parentWarehouseCtx.onWarehouseChange(undefined);
      }

      // Notify caller only if the warehouse actually changed from URL
      if (foundWarehouse?.id !== warehouseIdFromUrl) {
        onWarehouseChangeProp(foundWarehouse?.id);
      }
    },
    [onWarehouseChangeProp, warehousesMap, parentWarehouseCtx, warehouseIdFromUrl],
  );

  const contextValue = useWarehouseContextValue(enrichedWarehouse, onWarehouseChange);

  return (
    <WarehouseContext.Provider value={contextValue}>
      {contextValue.warehouse ? children(contextValue.warehouse) : fallback}
    </WarehouseContext.Provider>
  );
};

/**
 * Use the WarehouseContext to get the current warehouse and onWarehouseChange callback.
 * @returns The WarehouseContextType or a dummy context if the WarehouseContext is not available.
 */
export const useWarehouseContext = (): WarehouseContextType => {
  const context = useContext(WarehouseContext);
  return context;
};
